import type { LucideProps } from 'lucide-react';
import type { PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useToggle } from 'react-use';
import { Button } from '../../components/ui/button.tsx';
import { cn } from '../../lib/utils.ts';
import { DialogStateManager, useDialog } from './dialog-provider.tsx';
import { Dialog } from './dialog.tsx';
import type { DialogInnerProps } from './types.ts';

type Focus = 'submit' | 'destroy';

const ConfirmationDialogFocusContext = createContext<Focus>('submit');
const useConfirmationDialogFocus = (): Focus => useContext(ConfirmationDialogFocusContext);

type BaseConfirmationRejectButtonProps = {
    onClick: () => void;
} & PropsWithChildren;

const BaseConfirmationRejectButton = ({
    children,
    onClick,
}: BaseConfirmationRejectButtonProps): ReactNode => {
    return (
        <Button variant="subtle" onClick={onClick}>
            {children}
        </Button>
    );
};

type BaseConfirmationAcceptButtonProps = {
    isLoading?: boolean;
    onClick: () => void;
} & PropsWithChildren;

const BaseConfirmationAcceptButton = ({
    children,
    isLoading,
    onClick,
}: BaseConfirmationAcceptButtonProps): ReactNode => {
    const focus = useConfirmationDialogFocus();

    return (
        <Button
            variant={
                {
                    submit: 'primary',
                    destroy: 'danger',
                }[focus] as 'primary' | 'danger'
            }
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onClick}
        >
            {children}
        </Button>
    );
};

type BaseConfirmationDialogProps = {
    icon: React.ComponentType<LucideProps>;
    title: string;
    description: React.ReactNode;
    focus?: Focus;
    className?: string;
} & PropsWithChildren;

export const BaseConfirmationDialog = ({
    icon: Icon,
    focus = 'submit',
    title,
    description,
    children,
    className,
}: BaseConfirmationDialogProps) => {
    const { visible, remove } = useDialog();

    return (
        <ConfirmationDialogFocusContext.Provider value={focus}>
            <Dialog open={visible} onClose={remove} variant="compact">
                <Dialog.Header className="px-5 pt-4 sm:px-6">
                    <Dialog.Icon>
                        <Icon
                            className={cn(
                                'h-4 w-4',
                                {
                                    submit: 'text-blue-500',
                                    destroy: 'text-red-600',
                                }[focus],
                            )}
                        />
                    </Dialog.Icon>
                    <Dialog.HeaderText>
                        <Dialog.Title>{title}</Dialog.Title>
                    </Dialog.HeaderText>
                </Dialog.Header>
                <Dialog.Content className={cn('w-full gap-4 px-5 sm:px-6', className)}>
                    <Dialog.Description>{description}</Dialog.Description>
                    {children}
                </Dialog.Content>
            </Dialog>
        </ConfirmationDialogFocusContext.Provider>
    );
};

BaseConfirmationDialog.RejectButton = BaseConfirmationRejectButton;
BaseConfirmationDialog.AcceptButton = BaseConfirmationAcceptButton;
BaseConfirmationDialog.Actions = Dialog.Actions;

export type ConfirmationDialogProps = {
    accept: {
        text: string;
        action: () => Promise<void> | void;
    };
    reject: {
        text: string;
        action?: () => void;
    };
} & DialogInnerProps &
    BaseConfirmationDialogProps;

export const ConfirmationDialog = DialogStateManager.create(
    ({ icon, focus = 'submit', title, description, accept, reject, className }: ConfirmationDialogProps) => {
        const modal = useDialog();

        const [isLoading, toggleLoading] = useToggle(false);

        const handleAccept = async (): Promise<void> => {
            toggleLoading();

            try {
                await accept.action();
            } catch (e) {
                console.warn(e);
            }

            toggleLoading();
            void modal.hide();
        };

        const handleReject = (): void => {
            reject.action?.();
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                icon={icon}
                title={title}
                description={description}
                focus={focus}
                className={className}
            >
                <Dialog.Actions className="gap-2">
                    <BaseConfirmationDialog.RejectButton onClick={handleReject}>
                        {reject.text}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton isLoading={isLoading} onClick={handleAccept}>
                        {accept.text}
                    </BaseConfirmationDialog.AcceptButton>
                </Dialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
